/**
 * Sworn Declaration of Eligibility — the affidavit that every contestant
 * must sign to participate. Signing this replaces the need to upload
 * legal proof of marital status (marriage certificate, divorce decree,
 * death certificate).
 *
 * The `AFFIDAVIT_VERSION` is stored alongside the signature so that if
 * the text is updated in the future, we know which wording a contestant
 * agreed to. Bump the version string whenever the substantive content
 * changes.
 */

export const AFFIDAVIT_VERSION = '2026-05-v1';

export const AFFIDAVIT_TITLE = 'Sworn Declaration of Eligibility';

export const AFFIDAVIT_INTRO =
  'This Declaration is required for all contestants competing within the Idaho America Pageants system, including the titles of Mrs. Idaho America, Mrs. Idaho American, and Miss Idaho for America Strong. Its purpose is to confirm contestant eligibility requirements and protect the integrity of the Idaho America Pageants organization, its contestants, titleholders, sponsors, affiliates, and representatives.';

export interface AffidavitSection {
  heading: string;
  body?: string;
  bullets?: string[];
  subsections?: Array<{
    heading: string;
    body: string;
  }>;
}

export const AFFIDAVIT_SECTIONS: AffidavitSection[] = [
  {
    heading: '1. Division Eligibility',
    body:
      'Each division within the Idaho America Pageants system has specific eligibility requirements relating to marital status, and eligibility must be maintained throughout the competition process and, if selected, throughout the entirety of the title reign.',
    subsections: [
      {
        heading: 'Miss Division Eligibility',
        body:
          'Contestants competing for the title of Miss Idaho for America Strong must be legally single at the time of application and throughout participation in the competition. "Legally single" means the contestant has never been married, is legally divorced pursuant to a finalized court-issued divorce decree, or is legally widowed. Contestants in the Miss division may not be currently married, separated pending divorce, or involved in unresolved or pending divorce proceedings.',
      },
      {
        heading: 'Mrs. Division Eligibility',
        body:
          'Contestants competing for the titles of Mrs. Idaho America or Mrs. Idaho American must be legally and lawfully married at the time of application, during the competition, and throughout the entirety of the title reign, if selected. "Legally and lawfully married" means the contestant is currently married pursuant to a legally recognized and valid marriage that is not void, fraudulent, or otherwise legally invalid. Contestants in the Mrs. division must immediately disclose any change in marital status or the commencement of divorce or annulment proceedings to the organization.',
      },
    ],
  },
  {
    heading: '2. Affirmation of Legal Status',
    body: 'By signing below, I affirm and certify that:',
    bullets: [
      'The marital status I provided to the organization is truthful and accurate;',
      'I meet all eligibility requirements for the division in which I am competing;',
      'No legal impediment exists that would make me ineligible for participation;',
      'I have not withheld, concealed, or misrepresented information regarding my marital status.',
    ],
    subsections: [
      {
        heading: 'If competing in the Miss division, I further affirm that:',
        body:
          'I am not currently married; I am not separated pending divorce; any prior marriage has been fully dissolved by final court order prior to signing this declaration.',
      },
      {
        heading: 'If competing in the Mrs. division, I further affirm that:',
        body:
          'I am currently legally married; my marriage is legally recognized and valid; I am not legally divorced; and I am not knowingly misrepresenting the status or validity of my marriage.',
      },
    ],
  },
  {
    heading: '3. Required Documentation',
    body:
      'I understand and agree that the Idaho America Pageants organization may request documentation verifying eligibility, including but not limited to:',
    bullets: [
      'Certified divorce decrees;',
      'Marriage certificates;',
      'Court-issued legal records;',
      'Government-issued identification;',
      'Any additional documentation reasonably necessary to verify eligibility.',
    ],
  },
  {
    heading: '4. Duty to Disclose Changes',
    body:
      'I agree to immediately notify the Idaho America Pageants organization in writing if my marital status or legal eligibility changes at any point during the competition season or title reign. Failure to disclose changes in eligibility status may result in disciplinary action, disqualification, or removal of title.',
  },
  {
    heading: '5. Fraud, Misrepresentation & Disqualification',
    body:
      'Providing false, misleading, incomplete, or fraudulent information regarding my eligibility or marital status constitutes a material misrepresentation to the organization. If the organization determines that I have misrepresented my eligibility in any way, the Idaho America Pageants organization reserves the right to:',
    bullets: [
      'Immediately disqualify me from competition;',
      'Remove or revoke any title, crown, sash, awards, scholarships, prizes, appearance opportunities, compensation, or honors awarded to me;',
      'Publicly announce my disqualification or removal if deemed necessary by the organization;',
      'Require return of organization property, including crowns, sashes, banners, gifts, or sponsored items;',
      'Permanently prohibit me from future participation in any division of the Idaho America Pageants system.',
    ],
  },
  {
    heading: '6. Release of Liability',
    body:
      'I agree that the Idaho America Pageants organization, including its directors, staff, judges, sponsors, volunteers, affiliates, and representatives, shall not be held liable for any claims, damages, losses, disputes, reputational harm, or legal actions arising from my own false statements, omissions, or misrepresentations regarding eligibility.',
  },
  {
    heading: '7. Acknowledgment of Rules',
    body: 'I acknowledge that:',
    bullets: [
      'I have read and understand the eligibility requirements for my division;',
      'I understand that the organization is relying upon the truthfulness of my statements in determining eligibility;',
      'I understand that this declaration may be relied upon in making official titleholder determinations.',
    ],
  },
  {
    heading: '8. Oath & Certification',
    body:
      'By signing below, I declare under penalty of perjury under the laws of the State of Idaho that the statements contained in this declaration are true, complete, and correct. I understand that any false, misleading, incomplete, or fraudulent statement may result in disqualification, removal of title, forfeiture of awards or privileges, and other legal or equitable remedies available to the Idaho America Pageants organization under applicable law.',
  },
];
